import React, { useState, useEffect } from 'react';
import { Users, Copy, Star, Gift, CheckCircle, ExternalLink, MousePointer, Share2 } from 'lucide-react';
import { getClientReferralStats, getLoyaltyLevel, getNextLevelInfo, ensureReferralAndWelcomePoints } from '../../../services/referralService';
import { supabase } from '../../../lib/supabase';

const GOOGLE_REVIEW_URL = import.meta.env.VITE_GOOGLE_REVIEW_URL || '';

// ─── Heartbeat animation CSS ─────────────────────────────────────────────────
const heartbeatStyle = `
  @keyframes heartbeat {
    0% { transform: scale(1); }
    25% { transform: scale(1.2); }
    40% { transform: scale(1); }
    60% { transform: scale(1.2); }
    100% { transform: scale(1); }
  }
  .heartbeat {
    animation: heartbeat 1.4s ease-in-out infinite;
    display: inline-block;
  }
  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-16px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .fade-in-down {
    animation: fadeInDown 0.5s ease-out forwards;
  }
`;

export default function ReferralSection({ client }) {
  const [referralStats, setReferralStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [showWelcomeAnimation, setShowWelcomeAnimation] = useState(false);
  const [currentClient, setCurrentClient] = useState(client);

  useEffect(() => {
    if (client?.id) {
      initReferralSection();
    }
  }, [client?.id]);

  const initReferralSection = async () => {
    try {
      setLoading(true);

      // Step 1: Ensure referral code + welcome points (generates code if missing)
      const ensureResult = await ensureReferralAndWelcomePoints(client?.id);

      // If welcome points were just awarded, show animation
      if (ensureResult?.welcome_points?.status === 'awarded') {
        setShowWelcomeAnimation(true);
        setTimeout(() => setShowWelcomeAnimation(false), 6000);
      }

      // Step 2: Reload client data from Supabase to get updated points + code
      const { data: updatedClient } = await supabase
        ?.from('clients')
        ?.select('id, name, referral_code, loyalty_points_total, welcome_points_awarded, client_status')
        ?.eq('id', client?.id)
        ?.single();

      if (updatedClient) {
        setCurrentClient({ ...client, ...updatedClient });
      }

      // Step 3: Load referral stats
      const stats = await getClientReferralStats(client?.id);
      setReferralStats(stats);
    } catch (err) {
      console.error('Error initializing referral section:', err);
    } finally {
      setLoading(false);
    }
  };

  const referralCode = referralStats?.referral_code || currentClient?.referral_code || '';
  const referralLink = referralCode ? `${window.location.origin}/?ref=${referralCode}` : '';
  const points = currentClient?.loyalty_points_total || client?.loyalty_points_total || 0;
  const loyaltyLevel = getLoyaltyLevel(points);
  const nextLevel = getNextLevelInfo(points);

  const copyToClipboard = async (text, type = 'code') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      if (type === 'link') {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleWhatsAppShare = () => {
    if (!referralCode) return;
    const clientName = currentClient?.name || client?.name || 'Client';
    const refLink = `${window.location.origin}/?ref=${referralCode}`;
    const message = `Bonjour 👋\n\nJe te recommande ADT Express pour envoyer tes colis, barriques, cartons et électroménager de la France vers Abidjan.\n\nTu peux utiliser mon lien de parrainage :\n${refLink}\n\nIls proposent actuellement une offre été : barrique noire 270L offerte, transport tout compris à 160€ TTC.`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const handleGoogleReview = () => {
    if (!GOOGLE_REVIEW_URL) return;
    window.open(GOOGLE_REVIEW_URL, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="animate-pulse space-y-3">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div id="parrainage" className="space-y-4 sm:space-y-6 mb-4 sm:mb-6">
      <style>{heartbeatStyle}</style>

      {/* ─── Welcome Animation Banner ──────────────────────────────────────── */}
      {showWelcomeAnimation && (
        <div className="fade-in-down bg-gradient-to-r from-pink-500 to-red-500 rounded-xl shadow-lg p-5 text-white text-center">
          <div className="text-4xl mb-2">
            <span className="heartbeat">❤️</span>
          </div>
          <h3 className="text-xl font-bold mb-1">Bienvenue chez ADT Express !</h3>
          <p className="text-pink-100 text-sm">
            Vous recevez <span className="font-bold text-white">+5 points de fidélité</span> pour la création de votre espace client.
          </p>
          <p className="text-pink-200 text-xs mt-1">Merci de nous rejoindre 🎉</p>
        </div>
      )}

      {/* ─── Commercial Block (always visible) ────────────────────────────── */}
      <div className="bg-gradient-to-br from-green-600 to-green-800 rounded-xl shadow-md p-5 text-white">
        <div className="flex items-start gap-3 mb-4">
          <Share2 className="w-6 h-6 text-green-200 flex-shrink-0 mt-0.5" />
          <div>
            <h2 className="text-lg font-bold mb-1">Invitez vos proches à envoyer leurs colis avec ADT Express</h2>
            <p className="text-green-100 text-sm leading-relaxed">
              Partagez votre code parrainage dès maintenant sur WhatsApp. À chaque premier envoi confirmé par un filleul, vous gagnez des points fidélité.
            </p>
          </div>
        </div>
        <p className="text-green-100 text-sm mb-4">
          Vos proches peuvent envoyer leurs barriques, cartons, électroménager et colis de la France vers Abidjan avec un accompagnement simple, rapide et sécurisé.
        </p>
        <button
          onClick={handleWhatsAppShare}
          disabled={!referralCode}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-white text-green-700 rounded-lg hover:bg-green-50 transition-colors font-bold text-sm shadow disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
          </svg>
          Partager mon lien sur WhatsApp
        </button>
      </div>

      {/* ─── Loyalty Level Banner ──────────────────────────────────────────── */}
      <div className={`bg-gradient-to-r ${loyaltyLevel?.color} rounded-lg shadow-md p-4 sm:p-6 text-white`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm opacity-80">Votre niveau fidélité</p>
            <h3 className="text-2xl font-bold flex items-center gap-2">
              {loyaltyLevel?.badge} {loyaltyLevel?.label}
            </h3>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{points}</p>
            <p className="text-sm opacity-80">points</p>
          </div>
        </div>

        {/* Welcome points badge */}
        {(currentClient?.welcome_points_awarded || client?.welcome_points_awarded) && (
          <div className="mb-3 inline-flex items-center gap-1.5 px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
            <span className="heartbeat">❤️</span>
            +5 points de bienvenue offerts
          </div>
        )}

        {nextLevel?.nextLabel && (
          <div>
            <div className="flex justify-between text-sm mb-1 opacity-90">
              <span>Prochain niveau : {nextLevel?.nextLabel}</span>
              <span>{nextLevel?.pointsNeeded} pts restants</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-500"
                style={{ width: `${Math.min(nextLevel?.progress, 100)}%` }}
              />
            </div>
          </div>
        )}

        {!nextLevel?.nextLabel && (
          <p className="text-sm opacity-90 mt-1">🎉 Vous avez atteint le niveau maximum !</p>
        )}

        <div className="mt-3 grid grid-cols-4 gap-1 text-center text-xs opacity-80">
          <div className={`rounded px-1 py-0.5 ${points < 50 ? 'bg-white/30 font-bold' : ''}`}>🥉 Bronze<br/>0–49</div>
          <div className={`rounded px-1 py-0.5 ${points >= 50 && points < 100 ? 'bg-white/30 font-bold' : ''}`}>🥈 Argent<br/>50–99</div>
          <div className={`rounded px-1 py-0.5 ${points >= 100 && points < 200 ? 'bg-white/30 font-bold' : ''}`}>🥇 Or<br/>100–199</div>
          <div className={`rounded px-1 py-0.5 ${points >= 200 ? 'bg-white/30 font-bold' : ''}`}>👑 Ambass.<br/>200+</div>
        </div>
      </div>

      {/* ─── Referral Stats ────────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <MousePointer className="text-green-600" size={22} />
          <h2 className="text-lg font-bold text-gray-900">Statistiques de parrainage</h2>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center bg-blue-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-blue-700">{referralStats?.total_clicks || 0}</p>
            <p className="text-xs text-blue-600 font-medium">Clics sur mon lien</p>
          </div>
          <div className="text-center bg-green-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-green-700">{referralStats?.total_referrals || 0}</p>
            <p className="text-xs text-green-600 font-medium">Filleuls</p>
          </div>
          <div className="text-center bg-purple-50 rounded-lg p-3">
            <p className="text-2xl font-bold text-purple-700">{referralStats?.total_points_earned || 0}</p>
            <p className="text-xs text-purple-600 font-medium">Pts gagnés</p>
          </div>
        </div>
      </div>

      {/* ─── Referral Code Card ────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Gift className="text-green-600" size={22} />
          <h2 className="text-lg font-bold text-gray-900">Mon code parrain</h2>
        </div>

        {referralCode ? (
          <>
            {/* Code display */}
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-4">
              <p className="text-xs text-green-700 mb-1 font-medium">Votre code unique</p>
              <div className="flex items-center justify-between gap-3">
                <span className="text-2xl font-bold text-green-700 tracking-wider">{referralCode}</span>
                <button
                  onClick={() => copyToClipboard(referralCode, 'code')}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  {copied ? <CheckCircle size={16} /> : <Copy size={16} />}
                  {copied ? 'Copié !' : 'Copier'}
                </button>
              </div>
            </div>

            {/* Link display */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-gray-500 mb-1">Lien de parrainage</p>
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm text-gray-700 truncate flex-1">{referralLink}</span>
                <button
                  onClick={() => copyToClipboard(referralLink, 'link')}
                  className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-gray-500 hover:text-green-600 transition-colors text-xs border border-gray-200 rounded"
                  title="Copier le lien"
                >
                  {copiedLink ? <CheckCircle size={13} /> : <Copy size={13} />}
                  {copiedLink ? 'Copié' : 'Copier'}
                </button>
              </div>
            </div>

            {/* Points info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div className="bg-blue-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-blue-700">+20</p>
                <p className="text-xs text-blue-600 font-medium">points pour vous</p>
                <p className="text-xs text-blue-500">après validation commande</p>
              </div>
              <div className="bg-orange-50 rounded-lg p-3 text-center">
                <p className="text-2xl font-bold text-orange-700">+5</p>
                <p className="text-xs text-orange-600 font-medium">points bienvenue</p>
                <p className="text-xs text-orange-500">pour votre filleul</p>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleWhatsAppShare}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors font-medium text-sm"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Partager ADT Express
              </button>
              {GOOGLE_REVIEW_URL ? (
                <button
                  onClick={handleGoogleReview}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border-2 border-yellow-400 hover:bg-yellow-50 text-yellow-700 rounded-lg transition-colors font-medium text-sm"
                >
                  <Star size={18} className="text-yellow-500" />
                  Laisser un avis Google
                </button>
              ) : (
                <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-400 rounded-lg text-sm text-center">
                  Lien d'avis Google en cours de configuration
                </div>
              )}
            </div>
          </>
        ) : (
          /* Loading state while code is being generated */
          <div className="text-center py-6">
            <div className="animate-spin h-8 w-8 border-2 border-green-500 border-t-transparent rounded-full mx-auto mb-3"></div>
            <p className="text-gray-500 text-sm">Génération de votre code parrain en cours...</p>
          </div>
        )}
      </div>

      {/* ─── Google Review Rewards ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="text-yellow-500" size={22} />
          <h2 className="text-lg font-bold text-gray-900">Laisser un avis Google</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Partagez votre expérience ADT Express sur Google et gagnez des points fidélité !
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
            <p className="text-2xl font-bold text-yellow-700">+10</p>
            <p className="text-xs text-yellow-700 font-medium">Avis Google</p>
            <p className="text-xs text-yellow-600">sans photo</p>
          </div>
          <div className="bg-yellow-50 rounded-lg p-3 text-center border border-yellow-100">
            <p className="text-2xl font-bold text-yellow-700">+15</p>
            <p className="text-xs text-yellow-700 font-medium">Avis Google</p>
            <p className="text-xs text-yellow-600">avec photo</p>
          </div>
        </div>
        {GOOGLE_REVIEW_URL ? (
          <button
            onClick={handleGoogleReview}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors font-medium text-sm"
          >
            <ExternalLink size={16} />
            Écrire un avis sur Google
          </button>
        ) : (
          <div className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-400 rounded-lg text-sm text-center">
            Lien d'avis Google en cours de configuration
          </div>
        )}
      </div>

      {/* ─── Referral list (filleuls) ──────────────────────────────────────── */}
      {referralStats?.referrals && referralStats?.referrals?.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="text-green-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900">Mes filleuls</h2>
          </div>
          <div className="space-y-2">
            {referralStats?.referrals?.map((ref) => (
              <div key={ref?.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900 text-sm">{ref?.referred_client_name || 'Client'}</p>
                  <p className="text-xs text-gray-500">{ref?.reward_type === 'referral' ? 'Parrainage' : ref?.reward_type}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                    ref?.status === 'validated' ? 'bg-green-100 text-green-700' :
                    ref?.status === 'pending'? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {ref?.status === 'validated' ? `+${ref?.points_awarded} pts` :
                     ref?.status === 'pending' ? 'En attente' : ref?.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
